import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Clock, LogIn, LogOut } from "lucide-react";
import { toast } from "sonner";

function fmt(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString("es-ES", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function fmtTime(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleTimeString("es-ES", {
    hour: "2-digit",
    minute: "2-digit",
  });
}

function durationMin(a, b) {
  if (!a || !b) return 0;
  return Math.round((new Date(b).getTime() - new Date(a).getTime()) / 60000);
}

function fmtDur(min) {
  if (min <= 0) return "—";
  const h = Math.floor(min / 60);
  const m = min % 60;
  return h > 0 ? `${h} h ${m} min` : `${m} min`;
}

export default function TimeClock() {
  const { user } = useAuth();
  const [data, setData] = useState({ status: "out", events: [] });
  const [busy, setBusy] = useState(false);
  const [dateFrom, setDateFrom] = useState(() => {
    const d = new Date();
    d.setDate(1);
    return d.toISOString().slice(0, 10);
  });

  const load = async () => {
    try {
      const { data } = await api.get("/timeclock/me", { params: { date_from: dateFrom } });
      setData(data);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [dateFrom]);

  const punch = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/timeclock/punch", { notes: "" });
      toast.success(data.kind === "in" ? "Entrada registrada" : "Salida registrada");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  // Build day summary by pairing in/out events
  const dayMap = {};
  const eventsAsc = [...data.events].reverse();
  let openIn = null;
  for (const e of eventsAsc) {
    const day = (e.timestamp || "").slice(0, 10);
    if (!dayMap[day]) dayMap[day] = { day, in: null, out: null, total: 0, items: [] };
    dayMap[day].items.push(e);
    if (e.kind === "in") {
      if (!dayMap[day].in) dayMap[day].in = e.timestamp;
      openIn = e.timestamp;
    } else if (e.kind === "out") {
      dayMap[day].out = e.timestamp;
      if (openIn) {
        dayMap[day].total += durationMin(openIn, e.timestamp);
        openIn = null;
      }
    }
  }
  const days = Object.values(dayMap).sort((a, b) => b.day.localeCompare(a.day));
  const monthTotal = days.reduce((s, d) => s + d.total, 0);

  return (
    <div className="space-y-6" data-testid="timeclock-page">
      <div>
        <div className="text-xs tracking-[0.2em] uppercase text-[#6E7772] font-semibold">
          Tu jornada
        </div>
        <h1 className="font-heading text-3xl sm:text-4xl tracking-tight font-medium text-[#2A2F2C] mt-1">
          Fichaje de trabajo
        </h1>
        <p className="text-sm text-[#6E7772] mt-1">
          Pulsa el botón para registrar entrada o salida. {user?.name}, ID {user?.employee_id}.
        </p>
      </div>

      <Card className="border-[#E5E3DB] shadow-none bg-white">
        <CardContent className="p-8 flex flex-col sm:flex-row items-center justify-between gap-6">
          <div>
            <div className="text-xs tracking-wider uppercase text-[#6E7772] font-semibold">
              Estado actual
            </div>
            <div className="font-heading text-3xl mt-1" data-testid="clock-status">
              {data.status === "in" ? (
                <span className="text-[#4A5D4E]">Trabajando</span>
              ) : (
                <span className="text-[#6E7772]">Fuera de turno</span>
              )}
            </div>
            <div className="text-xs text-[#6E7772] mt-2">
              {data.events[0]
                ? `Último evento: ${data.events[0].kind === "in" ? "Entrada" : "Salida"} · ${fmt(data.events[0].timestamp)}`
                : "Sin registros todavía"}
            </div>
          </div>
          <Button
            data-testid="punch-btn"
            disabled={busy}
            onClick={punch}
            className={`h-16 px-8 text-base ${
              data.status === "in"
                ? "bg-[#C07F67] hover:bg-[#A86A52] text-white"
                : "bg-[#4A5D4E] hover:bg-[#3C4A3F] text-white"
            }`}
          >
            {data.status === "in" ? (
              <>
                <LogOut className="h-5 w-5 mr-2" />
                Fichar salida
              </>
            ) : (
              <>
                <LogIn className="h-5 w-5 mr-2" />
                Fichar entrada
              </>
            )}
          </Button>
        </CardContent>
      </Card>

      <Card className="border-[#E5E3DB] shadow-none bg-white">
        <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-3">
          <CardTitle className="font-heading font-medium text-lg flex items-center gap-2">
            <Clock className="h-4 w-4 text-[#4A5D4E]" /> Resumen del periodo
          </CardTitle>
          <div className="flex items-center gap-2">
            <Input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              data-testid="period-from"
              className="h-9 w-40"
            />
            <Badge className="bg-[#EAE6DF] text-[#2A2F2C]">
              Total: {fmtDur(monthTotal)}
            </Badge>
          </div>
        </CardHeader>
        <CardContent>
          {days.length === 0 ? (
            <div className="text-center text-[#6E7772] py-8">Sin registros en este periodo.</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-[#E5E3DB]">
                  <TableHead>Día</TableHead>
                  <TableHead>Entrada</TableHead>
                  <TableHead>Salida</TableHead>
                  <TableHead>Horas trabajadas</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {days.map((d) => (
                  <TableRow key={d.day} className="border-[#E5E3DB]">
                    <TableCell className="font-medium">{d.day}</TableCell>
                    <TableCell>{fmtTime(d.in)}</TableCell>
                    <TableCell>{d.out ? fmtTime(d.out) : <span className="text-[#C07F67]">— pendiente</span>}</TableCell>
                    <TableCell className="font-medium">{fmtDur(d.total)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {user?.role === "admin" && <AdminAllClock />}
    </div>
  );
}

function AdminAllClock() {
  const [events, setEvents] = useState([]);
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));

  useEffect(() => {
    api.get("/timeclock/all", {
      params: { date_from: date, date_to: date },
    }).then((r) => setEvents(r.data)).catch(() => {});
  }, [date]);

  return (
    <Card className="border-[#E5E3DB] shadow-none bg-white">
      <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-3">
        <CardTitle className="font-heading font-medium text-lg">
          Fichajes del equipo
        </CardTitle>
        <Input
          type="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          className="h-9 w-40"
          data-testid="admin-clock-date"
        />
      </CardHeader>
      <CardContent>
        {events.length === 0 ? (
          <div className="text-center text-[#6E7772] py-6">Sin fichajes en este día.</div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="border-[#E5E3DB]">
                <TableHead>Trabajador</TableHead>
                <TableHead>Tipo</TableHead>
                <TableHead>Hora</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {events.map((e) => (
                <TableRow key={e.id} className="border-[#E5E3DB]">
                  <TableCell className="font-medium">{e.user_name}</TableCell>
                  <TableCell>
                    <Badge className={e.kind === "in" ? "bg-[#D1E0D7] text-[#2A2F2C]" : "bg-[#F3E5D8] text-[#2A2F2C]"}>
                      {e.kind === "in" ? "Entrada" : "Salida"}
                    </Badge>
                  </TableCell>
                  <TableCell>{fmt(e.timestamp)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}
